import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon } from 'glints-aries';
import { noop } from 'lodash';
import { FormattedMessage, MessageDescriptor } from 'react-intl';

import { usePrevious } from 'src/common/hooks';
import { useScrollToRef } from 'src/common/hooks/ScrollRestoration/useScrollToRef';

import * as S from './ReadMore.sc';

type Props = {
  className?: string;
  childType?: 'text' | 'element';
  defaultOpen?: boolean;
  onChangeReadMore?: (isReadMore: boolean) => void;
  readMoreLabel?: MessageDescriptor;
  readLessLabel?: MessageDescriptor;
};

export const ReadMore: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  className,
  childType = 'text',
  defaultOpen = false,
  readMoreLabel = { id: 'read-more', defaultMessage: 'Read More' },
  readLessLabel = { id: 'read-less', defaultMessage: 'Read Less' },
  onChangeReadMore = noop,
}) => {
  const [isReadMore, setIsReadMore] = useState(defaultOpen);
  const toggleReadMore = () => setIsReadMore(!isReadMore);
  const ref = useRef(null);
  const prevIsReadMore = usePrevious(isReadMore);
  const scrollToRef = useScrollToRef();
  const scrollToLess = useCallback(() => {
    scrollToRef(ref);
  }, [scrollToRef]);

  const ChildrenWrapper =
    childType === 'text' ? S.TextWrapper : S.ElementWrapper;

  useEffect(
    function scrollToLessWhenReadLess() {
      if (ref?.current?.offsetTop > window?.pageYOffset) return;
      if (!isReadMore && prevIsReadMore) {
        scrollToLess();
      }
    },
    [isReadMore, prevIsReadMore, scrollToLess]
  );

  useEffect(() => {
    onChangeReadMore(isReadMore);
  }, [isReadMore, onChangeReadMore]);

  return (
    <S.Root ref={ref} className={className}>
      <ChildrenWrapper isReadMore={isReadMore}>{children}</ChildrenWrapper>
      <S.ButtonContainer>
        <S.Toggle onClick={() => toggleReadMore()}>
          {isReadMore ? (
            <>
              <ArrowUpIcon />
              <FormattedMessage {...readLessLabel} />
            </>
          ) : (
            <>
              <ArrowDownIcon />
              <FormattedMessage {...readMoreLabel} />
            </>
          )}
        </S.Toggle>
      </S.ButtonContainer>
    </S.Root>
  );
};
